<template>
  <div>
    <!-- Header card -->
    <b-card>
      <h1 class="d-flex align-items-center mb-2">
        <b-img
          :src="logoSrc"
          alt="provider logo"
          class="provider-logo mr-2"
        />
        {{ pageTitle }}
      </h1>
      <b-row>
        <b-col
          cols="3"
          class="mb-2"
        >
          <h4 class="mb-0 text-muted">
            Full Company Name
          </h4>
          <h3 class="mb-0">
            {{ pageSubtitle }}
          </h3>
        </b-col>
        <b-col
          cols="3"
          class="mb-2"
        >
          <h4 class="mb-0 text-muted">
            Address
          </h4>
          <h6 class="mb-0">
            1 Churchill Place<br>
            Canary Wharf<br>
            London, E14 5HP
          </h6>
        </b-col>
        <b-col
          cols="3"
          class="mb-2"
        >
          <b-row>
            <b-col
              cols="12"
              class="mb-2"
            >
              <h4 class="mb-0 text-muted">
                Telephone
              </h4>
              <h6 class="mb-0">
                +44 20 7116 1000
              </h6>
            </b-col>
            <b-col cols="12">
              <h4 class="mb-0 text-muted">
                Email
              </h4>
              <h6 class="mb-0">
                structured.products@barclays.com
              </h6>
            </b-col>
          </b-row>
        </b-col>
        <b-col cols="3">
          <b-row>
            <b-col cols="12">
              <h4 class="mb-0 text-muted">
                Website
              </h4>
              <h6 class="mb-0">
                <a
                  href="#"
                  target="_blank"
                >
                  {{ pageTitle }} website
                  <feather-icon
                    icon="ExternalLinkIcon"
                    size="14"
                    class="ml-25"
                    style="margin-bottom: 2px;"
                  />
                </a>
              </h6>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-card>

    <!-- Enquiry summary -->
    <div class="mt-2 mb-2">
      <p>
        You have made <strong>2</strong> enquiries to <strong>{{ pageTitle }}</strong>.
        <router-link :to="{ name: 'your-enquiries' }">
          Click here to view and manage your enquiries.
        </router-link>
      </p>
    </div>

    <!-- Products tabs -->
    <b-tabs
      pills
      cards
    >
      <b-tab active>
        <template #title>
          <feather-icon
            icon="BoxIcon"
            size="16"
            class="mr-50"
          />
          <span>All Products ({{ products.length }})</span>
        </template>
        <div class="d-flex py-0 justify-content-start align-items-center mb-3">
          <h5>{{ products.length }} results</h5>
        </div>
        <category-details-distribution :distributions="allDistributions" />
        <b-table
          :items="products"
          :fields="fields"
          responsive
          striped
          hover
        />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="ActivityIcon"
            size="16"
            class="mr-50"
          />
          <span>Live Products ({{ liveProducts.length }})</span>
        </template>
        <div class="d-flex py-0 justify-content-start align-items-center mb-3">
          <h5>{{ liveProducts.length }} results</h5>
        </div>
        <category-details-distribution :distributions="liveDistributions" />
        <b-table
          :items="liveProducts"
          :fields="fields"
          responsive
          striped
          hover
        />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="ClockIcon"
            size="16"
            class="mr-50"
          />
          <span>Current Products ({{ currentProducts.length }})</span>
        </template>
        <div class="d-flex py-0 justify-content-start align-items-center mb-3">
          <h5>{{ currentProducts.length }} results</h5>
        </div>
        <category-details-distribution :distributions="currentDistributions" />
        <b-table
          :items="currentProducts"
          :fields="fields"
          responsive
          striped
          hover
        />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="CheckCircleIcon"
            size="16"
            class="mr-50"
          />
          <span>Matured Products ({{ maturedProducts.length }})</span>
        </template>
        <div class="d-flex py-0 justify-content-start align-items-center mb-3">
          <h5>{{ maturedProducts.length }} results</h5>
        </div>
        <b-table
          :items="maturedProducts"
          :fields="fields"
          responsive
          striped
          hover
        />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import {
  BCard, BImg, BRow, BCol, BTabs, BTab, BTable,
} from 'bootstrap-vue'
import CategoryDetailsDistribution from '@/views/data/CategoryDetailsDistribution.vue'

const ALL_PRODUCTS = [
  {
    id: 2841,
    name: 'FTSE 100 Autocall March 2026',
    startdate: '3 Mar 2026',
    enddate: '3 Mar 2031',
    status: 'Live',
    type: 'Autocall',
  },
  {
    id: 2790,
    name: 'FTSE 100 Kick-Out December 2025',
    startdate: '15 Dec 2025',
    enddate: '15 Dec 2030',
    status: 'Live',
    type: 'Kick-Out',
  },
  {
    id: 2744,
    name: 'S&P 500 Capital Protected October 2025',
    startdate: '6 Oct 2025',
    enddate: '6 Oct 2029',
    status: 'Current',
    type: 'Capital Protected',
  },
  {
    id: 2701,
    name: 'Euro Stoxx 50 Reverse Convertible August 2025',
    startdate: '11 Aug 2025',
    enddate: '11 Aug 2027',
    status: 'Current',
    type: 'Reverse Convertible',
  },
  {
    id: 2658,
    name: 'FTSE 100 Autocall June 2025',
    startdate: '2 Jun 2025',
    enddate: '2 Jun 2030',
    status: 'Current',
    type: 'Autocall',
  },
  {
    id: 2512,
    name: 'FTSE 100 Kick-Out September 2023',
    startdate: '18 Sep 2023',
    enddate: '18 Sep 2026',
    status: 'Live',
    type: 'Kick-Out',
  },
  {
    id: 2389,
    name: 'S&P 500 Autocall March 2023',
    startdate: '7 Mar 2023',
    enddate: '7 Mar 2028',
    status: 'Live',
    type: 'Autocall',
  },
  {
    id: 2104,
    name: 'FTSE 100 Capital Protected November 2021',
    startdate: '22 Nov 2021',
    enddate: '22 Nov 2025',
    status: 'Matured',
    type: 'Capital Protected',
  },
  {
    id: 1876,
    name: 'Euro Stoxx 50 Kick-Out April 2020',
    startdate: '14 Apr 2020',
    enddate: '14 Apr 2024',
    status: 'Matured',
    type: 'Kick-Out',
  },
  {
    id: 1643,
    name: 'FTSE 100 Autocall January 2019',
    startdate: '21 Jan 2019',
    enddate: '21 Jan 2024',
    status: 'Matured',
    type: 'Autocall',
  },
]

export default {
  name: 'ProviderDetailsStatic',
  components: {
    BCard,
    BImg,
    BRow,
    BCol,
    BTabs,
    BTab,
    BTable,
    CategoryDetailsDistribution,
  },
  data() {
    return {
      logoSrc: `${process.env.VUE_APP_BACKEND_PATH}serveproviderlogo.cfm?id=1`,
      distributionData: {
        all: [
          {
            title: 'Product Type',
            subtitle: 'Distribution by product type',
            hideLogos: true,
            useIcons: true,
            data: [
              {
                name: 'Autocall', numberOfProducts: 4, id: 1, iconname: 'TrendingUpIcon', iconcolor: '#1a73e8',
              },
              {
                name: 'Kick-Out', numberOfProducts: 3, id: 2, iconname: 'ZapIcon', iconcolor: '#28c76f',
              },
              {
                name: 'Capital Protected', numberOfProducts: 2, id: 3, iconname: 'ShieldIcon', iconcolor: '#00cfe8',
              },
              {
                name: 'Reverse Convertible', numberOfProducts: 1, id: 4, iconname: 'RefreshCwIcon', iconcolor: '#ff9f43',
              },
            ],
          },
          {
            title: 'Underlying',
            subtitle: 'Distribution by underlying',
            hideLogos: true,
            useIcons: true,
            data: [
              { name: 'FTSE 100', numberOfProducts: 6, id: 10 },
              { name: 'Euro Stoxx 50', numberOfProducts: 2, id: 11 },
              { name: 'S&P 500', numberOfProducts: 2, id: 12 },
            ],
          },
          {
            title: 'Counterparty',
            subtitle: 'Distribution by counterparty',
            hideLogos: true,
            useIcons: true,
            data: [
              { name: 'Counterparty', numberOfProducts: 7, id: 20 },
              { name: 'Counterparty', numberOfProducts: 3, id: 21 },
            ],
          },
        ],
        live: [
          {
            title: 'Product Type',
            subtitle: 'Distribution by product type',
            hideLogos: true,
            useIcons: true,
            data: [
              {
                name: 'Autocall', numberOfProducts: 2, id: 1, iconname: 'TrendingUpIcon', iconcolor: '#1a73e8',
              },
              {
                name: 'Kick-Out', numberOfProducts: 2, id: 2, iconname: 'ZapIcon', iconcolor: '#28c76f',
              },
            ],
          },
          {
            title: 'Underlying',
            subtitle: 'Distribution by underlying',
            hideLogos: true,
            useIcons: true,
            data: [
              { name: 'FTSE 100', numberOfProducts: 3, id: 10 },
              { name: 'S&P 500', numberOfProducts: 1, id: 12 },
            ],
          },
          {
            title: 'Counterparty',
            subtitle: 'Distribution by counterparty',
            hideLogos: true,
            useIcons: true,
            data: [
              { name: 'Counterparty', numberOfProducts: 4, id: 20 },
            ],
          },
        ],
        current: [
          {
            title: 'Product Type',
            subtitle: 'Distribution by product type',
            hideLogos: true,
            useIcons: true,
            data: [
              {
                name: 'Autocall', numberOfProducts: 1, id: 1, iconname: 'TrendingUpIcon', iconcolor: '#1a73e8',
              },
              {
                name: 'Capital Protected', numberOfProducts: 1, id: 3, iconname: 'ShieldIcon', iconcolor: '#00cfe8',
              },
              {
                name: 'Reverse Convertible', numberOfProducts: 1, id: 4, iconname: 'RefreshCwIcon', iconcolor: '#ff9f43',
              },
            ],
          },
          {
            title: 'Underlying',
            subtitle: 'Distribution by underlying',
            hideLogos: true,
            useIcons: true,
            data: [
              { name: 'FTSE 100', numberOfProducts: 2, id: 10 },
              { name: 'Euro Stoxx 50', numberOfProducts: 1, id: 11 },
            ],
          },
          {
            title: 'Counterparty',
            subtitle: 'Distribution by counterparty',
            hideLogos: true,
            useIcons: true,
            data: [
              { name: 'Counterparty', numberOfProducts: 2, id: 20 },
              { name: 'Counterparty', numberOfProducts: 1, id: 21 },
            ],
          },
        ],
      },
      fields: [
        {
          key: 'id',
          label: 'ID',
          sortable: true,
        },
        {
          key: 'name',
          label: 'Product Name',
          sortable: true,
        },
        {
          key: 'startdate',
          label: 'Start Date',
          sortable: true,
        },
        {
          key: 'enddate',
          label: 'End Date',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'type',
          label: 'Type',
          sortable: true,
        },
      ],
      products: ALL_PRODUCTS,
    }
  },
  computed: {
    pageTitle() {
      const map = {
        underlyings: 'Underlying',
        'underlying-details': 'Underlying',
        issuers: 'Counterparty',
        'counterparty-details': 'Counterparty',
        'product-types': 'Product Type',
        'product-type-details': 'Product Type',
        content: 'Provider',
      }
      return map[this.$route.name] || 'Provider'
    },
    pageSubtitle() {
      const map = {
        underlyings: 'Underlying Asset',
        'underlying-details': 'Underlying Asset',
        issuers: 'Counterparty / Issuer',
        'counterparty-details': 'Counterparty / Issuer',
        'product-types': 'Product Type',
        'product-type-details': 'Product Type',
        content: 'Provider',
      }
      return map[this.$route.name] || 'Provider'
    },
    liveProducts() {
      return this.products.filter(p => p.status === 'Live')
    },
    currentProducts() {
      return this.products.filter(p => p.status === 'Current')
    },
    maturedProducts() {
      return this.products.filter(p => p.status === 'Matured')
    },
    allDistributions() {
      return this.distributionData.all
    },
    liveDistributions() {
      return this.distributionData.live
    },
    currentDistributions() {
      return this.distributionData.current
    },
  },
}
</script>

<style scoped>
.provider-logo {
  width: 48px;
  height: 48px;
  object-fit: contain;
}
</style>
